import React, { useState } from 'react'
import { 
  XMarkIcon,
  UserCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

interface User {
  id: number
  email: string
  fullName: string
  role: string
  employeeNumber?: string
}

interface ChangeRoleModalProps {
  isOpen: boolean
  onClose: () => void
  user: User | null
  onConfirm: (userId: number, newRole: string) => Promise<void>
  isLoading?: boolean
}

const ROLES = [
  { value: 'admin', label: 'Administrator', description: 'Full system access and user management' },
  { value: 'hr_manager', label: 'HR Manager', description: 'HR operations and employee management' },
  { value: 'hr_staff', label: 'HR Staff', description: 'Limited HR operations' },
  { value: 'finance_manager', label: 'Finance Manager', description: 'Financial operations and payroll' },
  { value: 'manager', label: 'Manager', description: 'Team management and approvals' },
  { value: 'employee', label: 'Employee', description: 'Standard employee access' }
]

const ChangeRoleModal: React.FC<ChangeRoleModalProps> = ({
  isOpen,
  onClose,
  user,
  onConfirm,
  isLoading = false
}) => {
  const [selectedRole, setSelectedRole] = useState('')

  React.useEffect(() => {
    if (user) {
      setSelectedRole(user.role)
    }
  }, [user])

  if (!isOpen || !user) return null

  const currentRoleInfo = ROLES.find(role => role.value === user.role)
  const newRoleInfo = ROLES.find(role => role.value === selectedRole)
  const isRoleChanged = selectedRole !== user.role

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isRoleChanged) {
      await onConfirm(user.id, selectedRole)
      onClose()
    }
  }

  const handleClose = () => {
    setSelectedRole(user?.role || '')
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                <UserCircleIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Change User Role
                </h3>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* User Details */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">User Details</label>
              <div className="bg-gray-50 rounded-md p-3">
                <p className="text-sm">
                  <span className="font-medium">Name:</span> {user.fullName}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Email:</span> {user.email}
                </p>
                {user.employeeNumber && (
                  <p className="text-sm">
                    <span className="font-medium">Employee #:</span> {user.employeeNumber}
                  </p>
                )}
                <p className="text-sm">
                  <span className="font-medium">Current Role:</span>{' '}
                  <span className="text-blue-600 font-medium">
                    {currentRoleInfo?.label || user.role}
                  </span>
                </p>
              </div>
            </div>

            {/* Role Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Role
              </label>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {ROLES.map((role) => (
                  <label
                    key={role.value}
                    className={`flex items-start p-3 border rounded-md cursor-pointer hover:bg-gray-50 ${
                      selectedRole === role.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={role.value}
                      checked={selectedRole === role.value}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      disabled={isLoading}
                    />
                    <div className="ml-3 min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">{role.label}</p>
                      <p className="text-xs text-gray-500 mt-1">{role.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Warning for Admin Role */}
            {selectedRole === 'admin' && user.role !== 'admin' && (
              <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                  <div className="text-sm text-yellow-700">
                    <p className="font-medium">Administrator Role Warning</p>
                    <p className="mt-1">
                      This user will have full system access including user management, 
                      system configuration, and all administrative functions.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Role Change Summary */}
            {isRoleChanged && (
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="text-sm text-blue-700">
                  <p className="font-medium">Role Change Summary:</p>
                  <p className="mt-1">
                    <span className="text-gray-600">From:</span>{' '}
                    <span className="font-medium">{currentRoleInfo?.label}</span>
                  </p>
                  <p>
                    <span className="text-gray-600">To:</span>{' '}
                    <span className="font-medium text-blue-600">{newRoleInfo?.label}</span>
                  </p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || !isRoleChanged}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </div>
                ) : (
                  'Update Role'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ChangeRoleModal